type BudgetCustomerAddressInput = {
  contactSources: any[];
  /**
   * Kept for API compatibility with existing callers. CEC-316 forbids using
   * company fallback data when rendering the customer/contact PDF address.
   */
  customerCompany?: any;
};

function hasValue(value: any): boolean {
  return value !== undefined && value !== null && value.toString().trim() !== '' && value.toString().trim() !== '-';
}

function cleanText(value: any): string {
  return hasValue(value) ? value.toString().trim() : '';
}

function firstText(...values: any[]): string {
  for (const value of values) {
    const cleanValue = cleanText(value);
    if (cleanValue) return cleanValue;
  }

  return '';
}

function getStreet(source: any): string {
  const city = cleanText(source?.city);
  const address = cleanText(source?.address);

  return firstText(
    source?.street,
    address && address !== city ? address : ''
  );
}

function hasContactAddressField(source: any): boolean {
  if (!source) return false;

  return !!getStreet(source)
    || !!cleanText(source.street2)
    || !!cleanText(source.city)
    || !!firstText(source.zip, source.postalCode)
    || !!firstText(source?.country?.name, source?.countryName);
}

export function buildBudgetCustomerAddressLines({ contactSources }: BudgetCustomerAddressInput): string[] {
  const addressSource = (contactSources || []).filter(Boolean).find(hasContactAddressField);

  if (!addressSource) return [];

  const customerCityAndZip = [
    cleanText(addressSource.city),
    firstText(addressSource.zip, addressSource.postalCode)
  ].filter(Boolean).join(' ');

  const customerCountry = firstText(
    addressSource?.country?.name,
    addressSource?.countryName
  );

  return [
    getStreet(addressSource),
    cleanText(addressSource.street2),
    customerCityAndZip,
    customerCountry
  ].filter(Boolean);
}

/**
 * Empresa y contacto en UNA sola línea, separados por coma (CEC-617).
 *
 * Los dos campos pueden faltar y ninguno de los dos casos es raro: cuando el
 * contacto ES la propia empresa, `samePartner()` deja `contact` vacío a
 * propósito, y `company` cae a su respaldo cuando la oportunidad no la trae.
 *
 * Une solo lo que tiene contenido, así que nunca deja una coma colgando ni una
 * coma inicial. Si no queda nada, devuelve cadena vacía y la plantilla no pinta
 * el elemento — que no es lo mismo que pintar una línea en blanco.
 *
 * `cleanText` descarta también el guion suelto, igual que en el resto del bloque
 * de cliente: es el marcador de «sin dato» que usa esta página.
 */
export function buildBudgetCustomerNameLine(company: any, contact: any): string {
  return [cleanText(company), cleanText(contact)].filter(Boolean).join(', ');
}
